import { useMap } from '@vis.gl/react-google-maps';
import React, { useEffect, useMemo } from 'react';
import { useModules } from '../../contexts';
import getMapType from '../../util/WmsMapType';
import { useMapContext } from '../../contexts/MapContext/MapContext';
import { WMSInterface } from '@schamburg-web/interfaces';

const resolveWmsMapLayer = (
  layer: unknown,
  mode: 'light' | 'dark'
): WMSInterface | undefined => {
  if (!layer) return undefined;
  if (typeof layer === 'object' && 'server' in (layer as object)) {
    return layer as WMSInterface;
  }

  const themedLayer = layer as { light?: WMSInterface; dark?: WMSInterface };
  return mode === 'dark'
    ? themedLayer.dark ?? themedLayer.light
    : themedLayer.light ?? themedLayer.dark;
};

const GoogleWmsLayers: React.FC<{
  serverHost: string;
  mode: 'light' | 'dark';
}> = ({ serverHost, mode }) => {
  const map = useMap();
  const { modules, modulesEnabled } = useModules();
  const {
    tilesLoaded: mapTilesLoaded,
    addMapMessage,
    removeMapMessage,
  } = useMapContext();

  // Memoize the enabled modules for this server to avoid redundant filtering
  const enabledModules = useMemo(
    () =>
      modules.filter((module) => {
        const mapLayer = resolveWmsMapLayer(module.mapConfig.wmsMapLayer, mode);
        return (
          !!mapLayer &&
          mapLayer.server === serverHost &&
          modulesEnabled[module.name]
        );
      }),
    [modules, modulesEnabled, serverHost, mode]
  );

  // Memoize layers and styles instead of using state
  const wmsEnabled = useMemo(() => {
    const layers: string[] = [];
    const styles: string[] = [];

    enabledModules.forEach((module) => {
      const mapLayer = resolveWmsMapLayer(module.mapConfig.wmsMapLayer, mode);
      if (!mapLayer) return;

      const moduleLayers = mapLayer.args.LAYERS ?? [];
      const moduleStyles = mapLayer.args.STYLES ?? [];

      moduleLayers.forEach((layerName, index) => {
        if (!layerName) return;

        layers.push(layerName);
        // WMS requires styles to be positionally aligned with layers.
        styles.push(moduleStyles[index] ?? '');
      });
    });

    return {
      layers,
      styles,
      // Include mode so light/dark switches always refresh the overlay.
      key: `${mode}_${layers.join(',')}_${styles.join(',')}`,
    };
  }, [enabledModules, mode]);

  // Track the last loaded key to prevent duplicate loads
  const lastLoadedKey = React.useRef<string>('');
  // Track the current message so we can remove it later
  const currentMessage = React.useRef<string | null>(null);
  // Ensures stale load cycles cannot clear the active loading popup.
  const activeLoadToken = React.useRef<number>(0);

  /** tiles loaded is required because map tiles are not loaded
   * when the map is first created, so the overlayMapType is not set correctly
   * and the map is not displayed correctly.
   *
   * We track the tilesLoaded state in the MapContext and set it to true when the map tiles have been
   * loaded
   */
  useEffect(() => {
    if (!map || !mapTilesLoaded) return;

    // If no modules enabled, immediately remove existing overlays
    if (enabledModules.length === 0) {
      if (currentMessage.current) {
        removeMapMessage(currentMessage.current);
        currentMessage.current = null;
      }
      lastLoadedKey.current = '';
      const overlayLength = map.overlayMapTypes.getLength();
      for (let i = overlayLength - 1; i >= 0; i--) {
        const overlay = map.overlayMapTypes.getAt(i);
        if (overlay && overlay.name === serverHost) {
          map.overlayMapTypes.removeAt(i);
        }
      }
      return;
    }

    // Skip only if this exact config is already present on the current map instance.
    if (lastLoadedKey.current === wmsEnabled.key) {
      const overlayLength = map.overlayMapTypes.getLength();
      for (let i = 0; i < overlayLength; i++) {
        const overlay = map.overlayMapTypes.getAt(i);
        if (overlay && overlay.name === serverHost) {
          return;
        }
      }
    }

    // Update the last loaded key
    lastLoadedKey.current = wmsEnabled.key;

    // If no layers enabled, just remove existing overlays
    if (wmsEnabled.layers.length === 0) {
      if (currentMessage.current) {
        removeMapMessage(currentMessage.current);
        currentMessage.current = null;
      }
      const overlayLength = map.overlayMapTypes.getLength();
      for (let i = overlayLength - 1; i >= 0; i--) {
        const overlay = map.overlayMapTypes.getAt(i);
        if (overlay && overlay.name === serverHost) {
          map.overlayMapTypes.removeAt(i);
        }
      }
      return;
    }

    // Increment token only now — we are committing to a real load with a loading message.
    // Incrementing earlier (before early returns) would invalidate in-flight tile callbacks
    // from the same cycle, causing the loading popup to never be dismissed.
    const loadToken = ++activeLoadToken.current;

    // Set loading message when starting to load new layer
    const layerMessage = `Loading ${
      enabledModules.length === 1 ? 'overlay' : 'overlays'
    }: ${enabledModules.map((module) => module.moduleLabel).join(', ')}`;

    // Remove old message if exists
    if (currentMessage.current) {
      removeMapMessage(currentMessage.current);
    }
    // Add new message and track it
    addMapMessage(layerMessage);
    currentMessage.current = layerMessage;

    let loadingTimeout: NodeJS.Timeout | null = null;
    let tilesLoadedFlag = false;
    const messageStartTime = Date.now();
    const MIN_MESSAGE_DURATION = 1000; // Show message for at least 1 second

    const clearLoadingState = () => {
      if (activeLoadToken.current !== loadToken) {
        return;
      }

      if (!tilesLoadedFlag) {
        tilesLoadedFlag = true;

        // Ensure minimum display time
        const elapsed = Date.now() - messageStartTime;
        const delay = Math.max(0, MIN_MESSAGE_DURATION - elapsed);

        setTimeout(() => {
          if (activeLoadToken.current !== loadToken) {
            return;
          }

          if (currentMessage.current) {
            removeMapMessage(currentMessage.current);
            currentMessage.current = null;
          }
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
            loadingTimeout = null;
          }
        }, delay);
      }
    };

    // Find and save reference to the old overlay BEFORE creating new one
    let oldOverlay: google.maps.MapType | null = null;
    const overlayLength = map.overlayMapTypes.getLength();
    for (let i = 0; i < overlayLength; i++) {
      const overlay = map.overlayMapTypes.getAt(i);
      if (overlay && overlay.name === serverHost) {
        oldOverlay = overlay;
        break;
      }
    }

    // Create new overlay
    const wmsLayer = getMapType(
      serverHost,
      wmsEnabled.layers,
      wmsEnabled.styles,
      () => {
        // Only called when all tiles are loaded
        // NOW remove old overlay by object reference (after new tiles loaded)
        if (oldOverlay) {
          // Find the current index of the old overlay
          const currentLength = map.overlayMapTypes.getLength();
          for (let i = 0; i < currentLength; i++) {
            const overlay = map.overlayMapTypes.getAt(i);
            if (overlay === oldOverlay) {
              map.overlayMapTypes.removeAt(i);
              break;
            }
          }
        }

        clearLoadingState();
      }
    );

    const newOverlay = new google.maps.ImageMapType(wmsLayer);

    // Fallback timeout in case tiles don't load within reasonable time
    loadingTimeout = setTimeout(() => {
      // Still remove old overlay on timeout
      if (oldOverlay) {
        const currentLength = map.overlayMapTypes.getLength();
        for (let i = 0; i < currentLength; i++) {
          const overlay = map.overlayMapTypes.getAt(i);
          if (overlay === oldOverlay) {
            map.overlayMapTypes.removeAt(i);
            break;
          }
        }
      }
      clearLoadingState();
    }, 10000); // 10 second timeout

    // Add new overlay (old overlay still present for now)
    map.overlayMapTypes.push(newOverlay);

    // Cleanup function for this cycle only.
    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [
    map,
    serverHost,
    wmsEnabled,
    mapTilesLoaded,
    enabledModules,
    addMapMessage,
    removeMapMessage,
  ]);

  return null;
};

export default GoogleWmsLayers;
